/* 🔒 The accounting documents' one stylesheet (piece 22; decisions 0409, 0612):
   the bar the two views are chosen on, the action bar, the sheet with its corner
   ribbon, the parties, the read-only line table and the blocks beneath it. The
   forms and the shared panels draw from it too, so a document reads the same
   whichever of its screens somebody is on.

   💰 It was four modules until 2026-09-21 — this one, the Bill's, the Purchase
   Order's and the Quote panels' — three of them near-identical copies with the
   line rows renamed, which is the drift decision 0612 exists to end. */
.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.pills {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

.sentence {
  margin: 0;
  color: var(--text-muted);
}

.rejection {
  margin: 0;
  color: var(--status-error-text);
}

.heading {
  margin: 0 0 var(--space-2);
  font-size: var(--font-size-subtitle);
  font-weight: var(--font-weight-semibold);
}

.muted {
  margin: 0;
  color: var(--text-muted);
}

/* 🔒 One line of the sheet's warning banner: the opening, each region, the fix (T591). */
.sheetWarningLine {
  display: block;
}

.block {
  padding: var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-medium);
  background: var(--surface);
}

/* 🔒 The document line grid (T3227): the read-only line table and the two forms' line blocks, the one set of rows here that is a table with tracks. Every other list of rows on a document page is the shared detail list. */
.lineList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* 🔒 A document's files draw as the one File row a Files tile draws (decision 3010). */
.fileList {
  list-style: none;
  margin: 0;
  padding: 0;
}

.row {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-1);
  padding-block: var(--space-2);
  border-bottom: 1px solid var(--border);
}

.lineMain {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.rowName {
  font-weight: var(--font-weight-semibold);
}

.rowLink {
  color: var(--navy-primary);
  text-decoration: none;
}

.rowNote {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.rowMiddle {
  color: var(--text);
}

/* 🔒 The heading row over a quantities table's columns (T182), in the same
   caption-size, muted, spaced capitals every other column heading in the
   application reads in. It carries the block's rule and never a row's.
   🔨 Below the breakpoint the row stacks into one column, so a heading row is a
   stacked list of words over a stacked list of values and says nothing; it is not
   drawn there, and each named cell reads its own column's name instead. */
.rowHead {
  display: none;
  padding-block-end: var(--space-1);
  border-bottom: 1px solid var(--border);
  color: var(--section-label-color);
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  letter-spacing: var(--section-label-tracking);
  text-transform: uppercase;
}

.rowMiddle[data-document-column]::before {
  content: attr(data-document-column) ": ";
  color: var(--text-muted);
}

.rowAmount {
  color: var(--text);
  font-weight: var(--font-weight-semibold);
}

/* 🔒 A sales line's list price beneath its unit price, in the smaller gray the
   sheet prints it in (decisions 1063, 10040). */
.cellSub {
  display: block;
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-regular);
}

/* 🔒 The sheet's taxable T beside a taxed line's amount (decisions 1062, 10040);
   a line not taxed keeps the space blank, so the figures stand in one column. */
.taxMark {
  margin-left: var(--space-1);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-bold);
}

.taxMarkBlank {
  visibility: hidden;
}

.taxLegend {
  margin: var(--space-1) 0 0;
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  text-align: right;
}

.rowAction {
  display: flex;
  justify-content: flex-start;
}

.total {
  margin: var(--space-2) 0 0;
  text-align: right;
  font-variant-numeric: var(--figures);
  font-weight: var(--font-weight-semibold);
}

.footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.refusal {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

/* 🔒 How many middle cells this kind's widest row draws, counted by the table and
   handed down as a number (found in review, 2026-09-21). The Quote draws five, the
   Invoice two, the Bill one, so a fixed set of tracks put three of the Quote's cells
   into implicit columns nothing below sized. */
.lineList {
  --document-row-middles: 2;
}

@media (--bp-md) {
  .row {
    grid-template-columns: minmax(0, 3fr) repeat(var(--document-row-middles), minmax(0, 1fr)) minmax(0, 1.2fr);
    align-items: start;
    gap: var(--space-3);
  }

  /* A kind with a control on the row — the Payment's Dissociate — gets a track for it,
     and every row of that kind carries the track so the money stays in one column. */
  .rowWithAction {
    grid-template-columns: minmax(0, 3fr) repeat(var(--document-row-middles), minmax(0, 1fr)) minmax(0, 1.2fr) auto;
  }

  /* 🔒 A document that moves quantities and no money has no amount column, so its
     rows take the leading cell and the middles alone (T182). */
  .rowNoAmount {
    grid-template-columns: minmax(0, 3fr) repeat(var(--document-row-middles), minmax(0, 1fr));
  }

  .rowMiddle,
  .rowAmount {
    text-align: right;
    font-variant-numeric: var(--figures);
  }

  .rowHead {
    display: grid;
  }

  .rowMiddle[data-document-column]::before {
    content: none;
  }

  .rowAction {
    justify-content: flex-end;
  }
}

/* The pencil on a document's own note row, drawn as a glyph rather than a labelled
   button, the way the record page's tiles draw theirs (decisions 0301, 0470). */
.rowEdit {
  width: var(--button-height-small);
  height: var(--button-height-small);
  padding: 0;
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  cursor: pointer;
}

/* 🔨 A row of controls under a block's own heading: flow, not the .row grid, which
   carries a bottom rule meant for list rows (decision 0469). */
.blockActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin-top: var(--space-2);
}

/* 🔒 The bar the two views are chosen on (decision 0612): the record's own tabs at
   the left, Details | PDF at the right. Under the md breakpoint the two stack, the
   choice first, because the choice is what a reader came for. */
.viewBar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

/*
 * 🔒 One control at the bar's right: the download chevron inside the left end of
 * the pill the two views are chosen on (T168). The two read as one control
 * because they share the track's own ground, so the row that carried a View PDF
 * link, a Download button and Edit is gone. 💰 That row was the product owner's
 * *"waste of space"*.
 */
.viewControl {
  display: inline-flex;
  align-items: center;
  border-radius: var(--radius-stadium);
  background: var(--status-inactive-bg);
}

/* 🔒 The switch at the head of the phone's one row (T2190; decision 3051): the
   record's tabs, where a kind has them, and the Details | PDF pill, with no cross. */
.viewSwitch {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--space-1);
}

/*
 * 🔒 The document's own footer bar (T168): the kind's moves in order and Edit at
 * the far right. It sits inside the shell's footer slot, which is what puts it on
 * the floor of the page. 💰 `documentFooter` and `actionBar` both drew a bar of
 * this shape before, in two stylesheets.
 */
.footerBar {
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

/* 🔒 One button size in the bar (decision 1452): every button the bar holds draws
   at Medium, whatever size its kind asked for. 💰 Start work and Terms were
   Medium while Override priority and Show the rate working were Small (UI audit
   of 2026-09-30, rank 9). */
.footerBar [data-size] {
  --button-height: var(--button-height-medium);
  --button-font: var(--font-size-button-medium);
}

/* 🔒 A figure or a hint in the bar stands on a line of its own above the buttons
   (decision 1452): whatever holds no button takes the bar's whole width and
   comes first, so the buttons read as one row. 💰 "0 h · $0.00" stood between
   two buttons and a lower-case hint sat under them. */
.footerBar > :not([data-size]):not(:has([data-size])) {
  order: -1;
  flex-basis: 100%;
}

.footerBarRight {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

/*
 * 🔒 On a phone the bar is two acts side by side and a full-width More under
 * them (T2190; decision 3052, the owner's P2a). The page lays every act out
 * with a mark: an element that only holds acts stands aside, a figure or a hint
 * reads first across the width, the rest stand above the two acts while More is
 * open, and More is the last row. 💰 A Draft Quote's six controls took three rows.
 */
.footerBar[data-document-actions-shape='phone'] {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: center;
}

/*
 * 🔨 An open More list is capped at half the screen and scrolls inside itself, so a document with six or more
 * acts never grows the footer past a short phone (T2235). The footer is `flex: none`, so its height is the bar's.
 */
.footerBar[data-document-actions-shape='phone'][data-document-more='open'] {
  max-height: 50vh;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.footerBar[data-document-actions-shape='phone'] [data-document-act='wrap'] {
  display: contents;
}

.footerBar[data-document-actions-shape='phone'] [data-document-act='note'] {
  grid-column: 1 / -1;
  order: -2;
}

.footerBar[data-document-actions-shape='phone'] [data-document-act='more'] {
  grid-column: 1 / -1;
  order: -1;
}

.footerBar[data-document-actions-shape='phone'][data-document-more='closed'] [data-document-act='more'] {
  display: none;
}

.footerBar[data-document-actions-shape='phone'] [data-document-act='lead'] {
  order: 0;
  min-width: 0;
}

.footerBar[data-document-actions-shape='phone'][data-document-leads='1'] [data-document-act='lead'] {
  grid-column: 1 / -1;
}

/* 🔒 Decision 3961: a footer of three first acts (the Service Report's clock, Add Note and Complete the Visit) lays its first across the row and the other two beneath it. */
.footerBar[data-document-actions-shape='phone'][data-document-leads='3'] [data-document-act-first] {
  grid-column: 1 / -1;
}

.footerBar[data-document-actions-shape='phone'] > [data-document-more-toggle] {
  grid-column: 1 / -1;
  order: 1;
  display: flex;
}

/* Every act and More fill their cell, the label centred in the main zone and a split's chevron kept at its end. */
.footerBar[data-document-actions-shape='phone'] [data-document-act='lead'],
.footerBar[data-document-actions-shape='phone'] [data-document-act='more'],
.footerBar[data-document-actions-shape='phone'] > [data-document-more-toggle] > [data-size] {
  width: 100%;
}

.footerBar[data-document-actions-shape='phone'] [data-document-act] > [data-zone='main'],
.footerBar[data-document-actions-shape='phone'] > [data-document-more-toggle] [data-zone='main'] {
  flex: 1 1 auto;
  min-width: 0;
}

/* The way out of a document, drawn as the cross it is rather than as a labelled
   button, the way a modal's own exit is drawn (piece 5). */
.close {
  width: var(--button-height-small);
  height: var(--button-height-small);
  padding: 0;
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: var(--font-size-subtitle);
  line-height: 1;
  cursor: pointer;
}

/* 🔒 The sheet, with the ribbon over its top right corner (decision 0612). The
   wrapper is what the ribbon is positioned against; the sheet inside it is drawn
   by the one region renderer and is untouched. */
.sheetWrap {
  position: relative;
}

/* 🔒 One scroller on the PDF view (T205; decision 0694): the page is the content
   region's own height, the sheet takes what the bars above it leave, and the pane
   inside it is what scrolls. 🔨 A percentage of the content region, as the
   calendar's page takes it (decision 0329), because the region is the shell's and
   a page does not reach into the shell's stylesheet. The Details view keeps the
   page's own height and scrolls the content region as every page does. */
.page[data-document-scroll='pane'] {
  height: 100%;
  min-height: 0;
}

.page[data-document-scroll='pane'] > .sheetWrap {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 🔒 The Internal Notes rail on the PDF view (T587; decision 1403): at the
   sheet's right from the tablet width up. On a phone it stands after the sheet in
   the page's flow, open, and the page is the one scroller (T2189; decision 3050,
   amending 1456). From the tablet width up the sheet keeps the one scroller of
   its own pane, and the rail scrolls its own notes inside it. */
.sheetWithRail {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.page[data-document-scroll='pane'] > .sheetWithRail {
  flex: 1 1 auto;
  min-height: 0;
}

.page[data-document-scroll='pane'] > .sheetWithRail > .sheetWrap {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.notesRail {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}

/* 🔒 In the page's flow on a phone the panel reads at its own height, so the
   page scrolls past it as it scrolls past the sheet (decision 3050). 💰 The
   drawer of decision 1456 was a strip at the pane's foot, and on a short phone
   the pane's floor outran the room the page left, so the strip stood on the
   sheet. */
.notesRail[data-document-notes-place='flow'] {
  overflow: visible;
}

/* Folded, the rail is its own small frame: the chevron and the title, nothing read. */
.notesRail[data-document-notes-rail='folded'] {
  flex-direction: row;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-medium);
  background: var(--surface);
}

/* The chevron a folding panel carries, the notes rail's and History's alike (decisions 1403, 1404). */
.foldToggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--button-height-small);
  height: var(--button-height-small);
  padding: 0;
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.foldToggle:hover {
  background: var(--surface-2);
  color: var(--text);
}

.foldToggle:focus-visible {
  outline: var(--focus-ring);
}

/* History's heading row: the title at the left, its chevron at the right (decision 1404). */
.foldHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-2);
}

/* 🔒 History's five-row window on a document page (T588; decision 1404): the list is held to five measured rows and scrolls inside. */
.historyWindow {
  overflow-y: auto;
}

/* Open, the chevron stands at the top right of the notes panel, where a tile's own fold stands. */
.notesRail[data-document-notes-rail='open'] > .foldToggle {
  position: absolute;
  top: var(--space-2);
  right: var(--space-2);
}

.notesRailLabel {
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
}

.notesRailBody {
  min-width: 0;
}

/* 🔨 An open rail whose notes draw nothing, a read refused to an outside login,
   stands no empty frame beside the sheet (decision 1403). 🔒 A read still in
   flight draws its heading, so the rail holds its width from the first paint and
   the sheet's fit is set once (T715; decision 1456). 🔒 A kind whose page read
   carried the notes as withheld mounts no rail at all, so a folded rail's strip
   never reaches an outside login (found in review, 2026-09-30); this rule is
   left for a kind that reads its notes itself. */
.notesRail[data-document-notes-rail='open']:has(> .notesRailBody:empty) {
  display: none;
}

@media (--bp-md) {
  .sheetWithRail {
    flex-direction: row;
    align-items: stretch;
  }

  .sheetWithRail > .sheetWrap {
    flex: 1 1 auto;
    min-width: 0;
  }

  .notesRail {
    max-height: none;
  }

  .notesRail[data-document-notes-rail='open'] {
    width: var(--document-notes-rail-width);
  }

  .notesRail[data-document-notes-rail='folded'] {
    flex-direction: column;
    align-items: center;
  }

  .notesRail[data-document-notes-rail='folded'] > .notesRailLabel {
    writing-mode: vertical-rl;
  }

  /* 🔒 Below 1280 beside the open list the rail stands under the sheet, open, as on a phone, so the sheet takes
     the document's whole column and the list stays (T3685; decision 11540). The page is the one scroller there. */
  .sheetWithRail[data-document-notes-place='flow'] {
    flex-direction: column;
  }

  .sheetWithRail[data-document-notes-place='flow'] > .notesRail[data-document-notes-rail='open'] {
    width: auto;
  }
}

/* 🔒 The status, in capitals, on a band across the sheet's top-left corner
   (decision 1457, amending 0612): the word along the band at 45 degrees, the band
   in the status's own strong colour with white text. It is a screen affordance
   and is not in the projection, so it cannot reach the paper.
   🔨 It is drawn in the paper's own inches inside the frame's corner layer,
   which scales with the sheet and clips to its edge, so the band keeps its place
   and size on the paper at every zoom step. The band's centre line runs from 1.15
   inches down the left edge to 1.15 inches along the top, across the sheet's
   margin corner and clear of the title at the top right. */
.ribbon {
  /* 🔒 Paper units, not screen units: the band sits in the layer scaled with the
     sheet, so it is measured in the paper's own inches and points and never takes
     a screen token (decision 1457). Its centre line runs from 1.15in down the left
     edge to 1.15in along the top; the offsets place a band of this length there. */
  --ribbon-length: 3in;
  --ribbon-thickness: 0.34in;
  --ribbon-top: 0.405in;
  --ribbon-left: -0.925in;
  --ribbon-font-size: 10pt;

  position: absolute;
  top: var(--ribbon-top);
  left: var(--ribbon-left);
  width: var(--ribbon-length);
  height: var(--ribbon-thickness);
  transform: rotate(-45deg);
  background: var(--status-inactive-text);
  color: var(--on-status-fill);
  font-size: var(--ribbon-font-size);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.12em;
  line-height: var(--ribbon-thickness);
  text-align: center;
  white-space: nowrap;
}

.ribbon[data-document-ribbon-group='good'] {
  background: var(--status-good-text);
}

.ribbon[data-document-ribbon-group='warning'] {
  background: var(--status-warning-text);
}

.ribbon[data-document-ribbon-group='error'] {
  background: var(--status-error-text);
}

/* 🔒 The notes panel is headed in the one section-label style on every document
   (decision 1452), the style a record page's tile headers read. 💰 It read
   "Internal Notes" on a Sales Order and "INTERNAL NOTES" on a Work Order. */
.sectionLabel {
  margin: 0 0 var(--space-2);
  color: var(--section-label-color);
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  letter-spacing: var(--section-label-tracking);
  text-transform: uppercase;
}

/* 🔒 The parties, one column per party under its own heading (decision 1120):
   stacked below the md breakpoint, side by side from it. The page hands the
   number of tracks, never fewer than two, so one party stands at half width. */
.parties {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-3);
}

.party {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.party p {
  margin: 0;
}

.partyAside {
  margin-top: var(--space-1);
}

/* 🔨 Past three parties, one row per party under one heading (decision 1120). */
.partyRows {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.partyRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.partyRow .partyAside {
  margin-top: 0;
  margin-left: auto;
}

/* The strongest of the totals under the line table, which is the last of them. */
.totalStrong {
  margin: var(--space-2) 0 0;
  text-align: right;
  font-variant-numeric: var(--figures);
  font-weight: var(--font-weight-bold);
}

@media (--bp-md) {
  .parties {
    grid-template-columns: repeat(var(--document-party-columns, 2), minmax(0, 1fr));
  }
}

/* 🔨 The variance sentence sits under the Bill line it is about, in the error
   group, so it is read with the figure it disagrees with (piece 24). */
.variance {
  color: var(--status-error-text);
  font-size: var(--font-size-caption);
}

/* 🔨 A skipped comparison says so rather than letting an absent flag read as a pass (decision 0413). */
.skipped {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

/* A control on a line of its own inside a row's second line, such as the Bill's Create asset. */
.assetAction {
  display: block;
  align-self: flex-start;
}

/* A line's printed reference or its description, in the row's own second line. */
.reference,
.description {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.working {
  margin-top: var(--space-2);
}

.workingToggle {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-small);
  background: var(--surface);
  color: var(--text-muted);
  font: inherit;
  font-size: var(--font-size-caption);
  cursor: pointer;
}

.workingLines {
  margin: var(--space-2) 0 0;
  padding-left: var(--space-4);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  line-height: var(--line-height-base);
}
